// components/FormFields.jsx - Application Details Form Fields
import React from 'react'
import { Plus, Check, User } from 'lucide-react'

const PRODUCT_UNITS = [
  { value: 'oz', label: 'oz' },
  { value: 'ml', label: 'mL' },
  { value: 'lb', label: 'lb' },
  { value: 'g', label: 'g' },
  { value: 'qt', label: 'qt' },
  { value: 'gal', label: 'gal' }
]

const CARRIER_UNITS = [
  { value: 'gal', label: 'gal' },
  { value: 'qt', label: 'qt' },
  { value: 'L', label: 'L' },
  { value: 'oz', label: 'oz' }
]

const CARRIER_TYPES = [
  { value: 'water', label: 'Water' },
  { value: 'oil', label: 'Oil-based' },
  { value: 'surfactant', label: 'With Surfactant' },
  { value: 'other', label: 'Other' }
]

// Convert between the stored en-US "M/D/YYYY" string and the ISO "YYYY-MM-DD"
// that <input type="date"> requires. Pure string ops: no timezone shift, no throw.
const mdyToISO = (mdy) => {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec((mdy || '').trim())
  if (!m) return ''
  const [, mo, d, y] = m
  return `${y}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}`
}
const isoToMDY = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec((iso || '').trim())
  if (!m) return ''
  const [, y, mo, d] = m
  return `${parseInt(mo, 10)}/${parseInt(d, 10)}/${y}`
}

export default function FormFields({
  formData,
  updateField,
  applicators,
  equipment,
  selectedApplicator,
  onSelectApplicator,
  requiresCarrier,
  showCarrier = requiresCarrier,  // Show the carrier section even when not required (e.g. optional water carrier for trunk injection)
  onAddEquipment,
  hideProductFields = false  // New: hide product amount section for multi-product mode
}) {
  return (
    <div className="p-4 space-y-6">
      {/* Product Amount - Hidden when using multi-product mode */}
      {!hideProductFields && (
        <div>
          <label className="form-label">
            Product Amount <span className="text-red-500">*</span>
          </label>
          <div className="flex gap-2">
            <input
              type="number"
              inputMode="decimal"
              step="0.1"
              min="0"
              placeholder="Amount"
              value={formData.productAmount}
              onChange={(e) => updateField('productAmount', e.target.value)}
              className="form-input flex-1"
            />
            <select
              value={formData.productUnit}
              onChange={(e) => updateField('productUnit', e.target.value)}
              className="form-select w-24"
            >
              {PRODUCT_UNITS.map(unit => (
                <option key={unit.value} value={unit.value}>{unit.label}</option>
              ))}
            </select>
          </div>
        </div>
      )}
      
      {/* Carrier (conditional) */}
      {showCarrier && (
        <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h4 className="text-sm font-medium text-blue-800">Carrier / Diluent</h4>

          <div>
            <label className="form-label">Carrier Type {requiresCarrier ? <span className="text-red-500">*</span> : <span className="text-gray-400 text-xs">(optional)</span>}</label>
            <select
              value={formData.carrierType}
              onChange={(e) => updateField('carrierType', e.target.value)}
              className="form-select"
            >
              <option value="">Select type...</option>
              {CARRIER_TYPES.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="form-label">Carrier Amount {requiresCarrier ? <span className="text-red-500">*</span> : <span className="text-gray-400 text-xs">(optional)</span>}</label>
            <div className="flex gap-2">
              <input
                type="number"
                inputMode="decimal"
                step="0.1"
                min="0"
                placeholder="Amount"
                value={formData.carrierAmount}
                onChange={(e) => updateField('carrierAmount', e.target.value)}
                className="form-input flex-1"
              />
              <select
                value={formData.carrierUnit}
                onChange={(e) => updateField('carrierUnit', e.target.value)}
                className="form-select w-24"
              >
                {CARRIER_UNITS.map(unit => (
                  <option key={unit.value} value={unit.value}>{unit.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}
      
      {/* Area or Plant Treated */}
      <div>
        <label className="form-label">
          Area or Plant Treated <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          placeholder="e.g., Front lawn, Oak tree #1, Garden beds"
          value={formData.areaOrPlantTreated}
          onChange={(e) => updateField('areaOrPlantTreated', e.target.value)}
          className="form-input"
        />
        <p className="text-xs text-gray-500 mt-1">
          Describe the specific location or plants treated
        </p>
      </div>
      
      {/* Equipment Selection */}
      <div>
        <label className="form-label">
          Equipment Used <span className="text-red-500">*</span>
        </label>
        
        {equipment.length > 0 ? (
          <div className="space-y-2">
            {equipment.map(item => (
              <button
                key={item.name}
                onClick={() => updateField('equipment', item.name)}
                className={`w-full p-3 rounded-lg border text-left flex items-center justify-between
                  ${formData.equipment === item.name 
                    ? 'border-arbor-500 bg-arbor-50' 
                    : 'border-gray-200 hover:border-gray-300'
                  }`}
              >
                <span className="font-medium">{item.name}</span>
                {formData.equipment === item.name && (
                  <Check className="w-5 h-5 text-arbor-500" />
                )}
              </button>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 italic">No equipment configured</p>
        )}
        
        <button
          onClick={onAddEquipment}
          className="mt-2 w-full py-2 border-2 border-dashed border-gray-300 rounded-lg
                     text-gray-600 flex items-center justify-center gap-2 text-sm
                     hover:border-arbor-400 hover:text-arbor-600 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Equipment
        </button>
      </div>
      
      {/* Applicator Selection */}
      <div>
        <label className="form-label">
          Applicator <span className="text-red-500">*</span>
        </label>
        
        {applicators.length > 0 ? (
          <div className="space-y-2">
            {applicators.map(applicator => (
              <button
                key={applicator.name}
                onClick={() => onSelectApplicator(applicator)}
                className={`w-full p-3 rounded-lg border text-left flex items-center gap-3
                  ${selectedApplicator === applicator.name 
                    ? 'border-arbor-500 bg-arbor-50' 
                    : 'border-gray-200 hover:border-gray-300'
                  }`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center
                  ${selectedApplicator === applicator.name 
                    ? 'bg-arbor-500 text-white' 
                    : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  <User className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <div className="font-medium">{applicator.name}</div>
                  <div className="text-sm text-gray-500">License: {applicator.licenseNumber}</div>
                </div>
                {selectedApplicator === applicator.name && (
                  <Check className="w-5 h-5 text-arbor-500" />
                )}
              </button>
            ))}
          </div>
        ) : (
          <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200 text-sm text-yellow-800">
            No applicators configured. Add applicators in the Google Sheet.
          </div>
        )}
      </div>
      
      {/* Date & Time */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="form-label">Date</label>
          <input
            type="date"
            value={mdyToISO(formData.date)}
            onChange={(e) => {
              const mdy = isoToMDY(e.target.value)   // '' when the field is empty/partial
              if (mdy) updateField('date', mdy)      // ignore empty -> never store "Invalid Date"
            }}
            className="form-input"
          />
        </div>
        <div>
          <label className="form-label">Time</label>
          <input
            type="time"
            value={(() => {
              if (!formData.time) return ''
              // Convert 12-hour format to 24-hour for the time input
              const timeStr = formData.time.trim()
              const match = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i)
              if (match) {
                let hours = parseInt(match[1])
                const minutes = match[2]
                const period = match[3].toUpperCase()

                if (period === 'PM' && hours !== 12) {
                  hours += 12
                } else if (period === 'AM' && hours === 12) {
                  hours = 0
                }

                return `${hours.toString().padStart(2, '0')}:${minutes}`
              }
              return ''
            })()}
            onChange={(e) => {
              const v = e.target.value           // "HH:mm" (24h) or "" when cleared
              if (!v) return
              const [hours, minutes] = v.split(':').map(Number)
              const period = hours >= 12 ? 'PM' : 'AM'
              const h12 = hours % 12 || 12
              updateField('time', `${h12}:${String(minutes).padStart(2, '0')} ${period}`)
            }}
            className="form-input"
          />
        </div>
      </div>
    </div>
  )
}
